import * as moment from 'moment';
import * as _ from 'lodash';
import { StepOneModel } from '../camp-sign-up/redux/reducers/step-one-reducer';
import { StepTwoModel } from '../camp-sign-up/redux/reducers/step-two-reducer';
import { StepThreeModel } from '../camp-sign-up/redux/reducers/step-three-reducer';
import { StepFourModel } from '../camp-sign-up/redux/reducers/step-four-reducer';
import { ParentSP } from './index';
import { Guest } from './rsvp';

export enum Gender {
    M = "M",
    F = "F",
    OTHER = "Other"
}

/**
 * Aggregate all form fields to this type.
 */
export type FormFields = StepOneFormFields & StepTwoFormFields & StepThreeFormFields & StepFourFormFields & StepOneModel & StepTwoModel & StepThreeModel & StepFourModel;

export type StepOneFormFields = IBasicInformation & IParent & ISibling & IParentInformation & IAboutMyChild;

/**
 * The interface representing the structure of the BasicInformation section.
 */
export interface IBasicInformation {
    sessionKey: string;
    student_cust_no: string;
    camp: string;
    payFullAmount: boolean;
    firstName: string;
    lastName: string;
    nickname: string;
    birthDate: Date;
    shirtSize: string;
    grade: string;
    school: string;
    gender: Gender;
}


export interface IParentInformationFormSubmit {
    parents: Array<ParentSP>;
}

export interface IParentInformation {
    // Must be an object because deep copying an array is much more involved.
    parents: Array<IParent>;
    sendCorrespondenceTo: string;
}

export interface IAboutMyChild {
    interests: string;
    details: string;
    moreDetails: string;
}


export interface IPhone {
    number: string;
    isPrimary: boolean;
}

export const defaultPhone: IPhone = {
    number: null,
    isPrimary: false
}

export interface IParent {
    firstName: string;
    lastName: string;
    address: string;
    city: string;
    state: string;
    zip: string;
    homePhone: IPhone;
    workPhone: IPhone;
    cellPhone: IPhone;
    email: string;
}

export const defaultParent: IParent = {
    firstName: null,
    lastName: null,
    address: null,
    city: null,
    state: null,
    zip: null,
    homePhone: defaultPhone,
    workPhone: defaultPhone,
    cellPhone: defaultPhone,
    email: null
}

export const defaultParentInformation: IParentInformation = {
    parents: [defaultParent],
    sendCorrespondenceTo: "0"
}

export const defaultBasicInformation: IBasicInformation = {
    sessionKey: "",
    student_cust_no: "",
    camp: "",
    payFullAmount: true,
    firstName: null,
    lastName: null,
    nickname: null,
    birthDate: moment().toDate(),
    shirtSize: null,
    grade: null,
    school: null,
    gender: null
}

export const defaultAboutMyChild: IAboutMyChild = {
    interests: '',
    details: '',
    moreDetails: ''
}


export interface ISibling {
    key: number;
    firstName: string;
    lastName: string;
    birthDate: Date;
}

export const defaultSibling: ISibling = {
    key: 0,
    firstName: "",
    lastName: "",
    birthDate: moment().toDate()
}

export interface IOption {
    text: string;
    value: string;
}

export interface IFormAnswer {
    id: keyof FormFields | keyof Guest;
    value: any;
}

/**
 * Step two form contents.
 */
export type StepTwoFormFields = IEmergencyContact & IHealthCareContact & IInsuranceInformation & IPickupAuthorization & ITransportationPermission;

export interface IEmergencyContact {
    firstName: string;
    lastName: string;
    relationship: string;
    phone: string;
}

export const defaultEmergencyContact: IEmergencyContact = {
    firstName: null,
    lastName: null,
    relationship: null,
    phone: null
};

export interface IHealthCareContact {
    physicianName: string;
    physicianPhone: string;
    dentistName: string;
    dentistPhone: string;
}

export const defaultHealthCareContact: IHealthCareContact = {
    physicianName: '',
    physicianPhone: '',
    dentistName: '',
    dentistPhone: ''
}

export interface IInsuranceInformation {
    healthPlan: string;
    policyHolderName: string;
    policyNumber: string;
}

export const defaultInsuranceInformation: IInsuranceInformation = {
    healthPlan: '',
    policyHolderName: '',
    policyNumber: ''
}

export interface IPickupAuthorization {
    firstName: string;
    lastName: string;
    phone: string;
}

export const defaultPickupAuthorization: IPickupAuthorization = {
    firstName: '',
    lastName: '',
    phone: ''
}

export interface ITransportationPermission {
    includeNameInCarpoolList: boolean;
    canTakePublicTransportation: boolean;
    canWalkHome: boolean;
}

export const defaultTransportationPermission: ITransportationPermission = {
    includeNameInCarpoolList: null,
    canTakePublicTransportation: null,
    canWalkHome: null
}

/**
 * Step three form contents.
 */
export type StepThreeFormFields = IAllergy & IEpiPen & IAllergySection & IDietaryRestriction & IMedicalCondition & IMedicalConditionSection & ISpecialNeedsSection & IMedication;

export interface IBaseMedicalCondition {
    type?: string;
    selected: boolean;
    description?: string;
}

export interface IAllergy extends IBaseMedicalCondition {
}

export const defaultAllergy: IAllergy = {
    type: '',
    selected: null,
    description: ""
}

export interface IDietaryRestriction extends IBaseMedicalCondition {
}

export const defaultDietaryRestriction: IDietaryRestriction = {
    type: '',
    selected: null
}

export interface IMedicalCondition extends IBaseMedicalCondition {
    childHasMedication?: boolean;
    canSelfAdminister?: boolean;
    keptWithChild?: boolean;
}

export const defaultMedicalCondition: IMedicalCondition = {
    type: '',
    selected: null,
    description: null,
    childHasMedication: null,
    canSelfAdminister: null,
    keptWithChild: null
}

export interface IEpiPen {
    relevantAllergies: string;
    childHasMedication: boolean;
    canSelfAdminister: boolean;
    keptWithChild: boolean;
}

export const defaultEpiPen: IEpiPen = {
    relevantAllergies: '',
    childHasMedication: null,
    canSelfAdminister: null,
    keptWithChild: null
}

export interface IAllergySection {
    allergies: Array<IAllergy>;
    epiPenInformation: IEpiPen;
    allergenFreeLunchGroup: boolean;
}

export const defaultAllergies: Array<IAllergy> = [
    _.assign({}, defaultAllergy, { type: "Peanuts" }),
    _.assign({}, defaultAllergy, { type: "Tree Nuts" }),
    _.assign({}, defaultAllergy, { type: "Other Foods" }),
    _.assign({}, defaultAllergy, { type: "Medication" }),
    _.assign({}, defaultAllergy, { type: "Bees" }),
    _.assign({}, defaultAllergy, { type: "Outdoor Allergies / Plants" }),
    _.assign({}, defaultAllergy, { type: "Other" })
]

export const defaultAllergySection: IAllergySection = {
    allergies: defaultAllergies,
    epiPenInformation: _.assign({}, defaultEpiPen),
    allergenFreeLunchGroup: null
}

export interface IDietaryRestrictionSection {
    dietaryRestrictions: Array<IDietaryRestriction>;
}

export interface IMedicalConditionSection {
    medicalConditions: Array<IMedicalCondition>;
    badActivities: string;
    call: boolean;
}

export const defaultDietaryRestrictionsArray: Array<IDietaryRestriction> = [
    _.assign({}, defaultDietaryRestriction, { type: "Gluten Free" }),
    _.assign({}, defaultDietaryRestriction, { type: "Vegetarian" }),
    _.assign({}, defaultDietaryRestriction, { type: "Vegan" }),
    _.assign({}, defaultDietaryRestriction, { type: "Lactose Intolerant" }),
    _.assign({}, defaultDietaryRestriction, { type: "Kosher" }),
    _.assign({}, defaultDietaryRestriction, { type: "Can your child eat pizza?" })
];

export const defaultMedicalConditionSection: IMedicalConditionSection = {
    medicalConditions: [
        _.assign({}, defaultMedicalCondition, { type: "Asthma" }),
        _.assign({}, defaultMedicalCondition, { type: "Diabetes" }),
        _.assign({}, defaultMedicalCondition, { type: "Epilepsy / Seizure" }),
        _.assign({}, defaultMedicalCondition, { type: "Dizziness / Fainting" }),
        _.assign({}, defaultMedicalCondition, { type: "Significant medical or surgery history?" }),
        _.assign({}, defaultMedicalCondition, { type: "Headaches" }),
        _.assign({}, defaultMedicalCondition, { type: "Other Medical Info" })
    ],
    badActivities: null,
    call: null
}

export interface ISpecialNeedsSection {
    selected: boolean;
    description: string;
}

export const defaultSpecialNeedsSection: ISpecialNeedsSection = {
    selected: null,
    description: ''
}

export interface IMedication {
    key: number;
    name: string;
    dosage: string;
    purpose: string;
}

export const defaultMedication: IMedication = {
    key: 0,
    name: '',
    dosage: '',
    purpose: ''
}

export interface IMedicationSection {
    medications: Array<IMedication>;
    administerAtCamp: string;
}

export const defaultMedicationSection: IMedicationSection = {
    medications: [defaultMedication],
    administerAtCamp: null
}

export interface IAdministeringMedication {
    authorize: boolean;
    initials: string;
    medications: string;
}

export const defaultAdministeringMedication: IAdministeringMedication = {
    authorize: null,
    initials: null,
    medications: ""
}

/**
 * Step four form contents.
 */
export type StepFourFormFields = IWaiver;

export interface IWaiver {
    authorize: boolean;
    initials: string;
}

export const defaultWaiver: IWaiver = {
    authorize: null,
    initials: ''
}


// WEBPACK FOOTER //
// ./app/classes/form-data.ts